import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import {Bank, BankAccount, DepositReduxState} from "core/redux/deposit/DepositReduxState";
import DepositEngine from "core/engine/DepositEngine";
import {
    DoSubmitDepositCCType,
    SelectDepositBank,
    SelectDepositBankAccount,
    SelectDepositBankAccountPayload,
    SelectDepositBankPayload,
    SetCreditCard,
    SetCreditCardPayload,
    SetCreditCardProvider,
    SetCreditCardProviderPayload, SetCreditCardValid, SetCreditCardValidPayload,
    SetDepositMinAmount,
    SetDepositMinAmountPayload,
    SetPaymentMethod,
    SetPaymentMethodAmount,
    SetPaymentMethodAmountPayload,
    SetPaymentMethodPayload, SetPredefinedAmounts, SetPredefinedAmountsPayload,
    SetWireTransferCurrency, SetWireTransferCurrencyPayload,
} from "core/redux/deposit/DepositReduxActions";
import {PaymentMethod} from "enum/PaymentMethod";
import {CreditCardType} from "enum/CreditCardType";
import Utils from "platform/util/Utils";
import {Currency} from "platform/enum/Currency";
import {TSMap} from "typescript-map";

export const DEF_CREDIT_CARD_LENGTH: number = 20;

export default class DepositReducer extends Reducer<DepositReduxState> {

    private static _instance: DepositReducer;

    public static instance(): DepositReducer {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        super();
        const depositEngine: DepositEngine = Platform.engine(ServiceType.Deposit);
        this._middlewareActions.set(DoSubmitDepositCCType, depositEngine.doSubmitDepositCC);
    }

    public get name(): string {
        return "deposit";
    }

    protected setup(builder: ReducerBuilder<DepositReduxState>): void {
        const banks: TSMap<Currency, Bank[]> = new TSMap<Currency, Bank[]>();
        banks.set(Currency.EUR, [
            {
                bankName: "Eurobank Ergasias S.A.",
                account: {
                    accountNumber: "0026.0025.49.0201991049",
                    address: "8 Othonos str., Athens, 105 57 Greece",
                    currency: "EUR",
                    beneficiary: "Vie Finance A.E.P.E.Y. ",
                    iban: "GR9802600250000490201991049",
                    swift: "ERBKGRAA"
                }
            },
            {
                bankName: "Alpha Bank",
                account: {
                    accountNumber: "157 00 2330 001391",
                    address: "48 Vas. Sophias,Kastalia Sq.Marousi 15124Greece",
                    currency: "EUR",
                    beneficiary: "Vie Finance A.E.P.E.Y. S.A.",
                    iban: "GR4701401570157002330001391",
                    swift: "CRBAGRAA"
                }
            },
            {
                bankName: "Piraeus Bank",
                account: {
                    accountNumber: "5083 098283863",
                    address: "27 Helmou str., Ag. Stefanos, Attica, Athens 145 65 Greece",
                    currency: "EUR",
                    beneficiary: "Vie Finance A.E.P.E.Y. S.A.",
                    iban: "GR3701720830005083098283863",
                    swift: "PIRBGRAA"
                }
            },
        ]);
        banks.set(Currency.USD, [
            {
                bankName: "Alpha Bank",
                account: {
                    accountNumber: "222 99 2330 005577",
                    address: "48 Vas. Sophias,Kastalia Sq.Marousi 15124Greece",
                    currency: "USD",
                    beneficiary: "Vie Finance A.E.P.E.Y. S.A.",
                    iban: "GR887701570157002330002211",
                    swift: "BBBAGRAA"
                }
            },
            {
                bankName: "Piraeus Bank",
                account: {
                    accountNumber: "8899 098284444",
                    address: "27 Helmou str., Ag. Stefanos, Attica, Athens 145 65 Greece",
                    currency: "USD",
                    beneficiary: "Vie Finance A.E.P.E.Y. S.A.",
                    iban: "GR3887720830005083098255566",
                    swift: "PIRBGRAA"
                }
            },
        ]);
        const selectedBankAccount: BankAccount = banks.get(Currency.EUR)[0].account;
        const selectedBank: Bank =  banks.get(Currency.EUR)[0];
        builder
            .init({
                paymentMethod: PaymentMethod.CreditCard,
                minAmount: 180,
                predefinedAmounts: [180, 300, 500],
                creditCardType: CreditCardType.Mastercard,
                supportedCurrencies: [Currency.EUR, Currency.USD],
                supportedPaymentMethods: [
                    PaymentMethod.CreditCard,
                    PaymentMethod.WireTransfer,
                    PaymentMethod.Trustly,
                    PaymentMethod.Paypal,
                    PaymentMethod.Neteller,
                    PaymentMethod.EasyEFT,
                    PaymentMethod.EcoPayz,
                    PaymentMethod.Sofort,
                    PaymentMethod.AstroPay,
                ],
                supportedCreditCards: [CreditCardType.Mastercard, CreditCardType.Visa],
                creditCard: {
                    amount: 210,
                    currency: Currency.EUR,
                    cardName: "Dmitriy Sumskoy",
                    cardExpirationMonth: new Date().getMonth() + 1,
                    cardExpirationYear: new Date().getFullYear()
                },
                paymentMethodAmount: {
                    amount: 300,
                    currency: Currency.EUR,
                },
                maxCardLength: DEF_CREDIT_CARD_LENGTH,
                wireTransfer: {
                    selectedBank,
                    selectedBankAccount,
                    banks,
                },
                wireTransferCurrency: Currency.EUR,
                receiptNumber: "123456789",
                transactionId: "234567890",
                transactionCardNumber: "4242424242424242",
            })
            .handle(SetPaymentMethod, (state: DepositReduxState, {payload}: Action<SetPaymentMethodPayload>) => {
                this._logger.debug("Set payment method: " + payload.paymentMethod);
                return Object.assign({}, state, {
                    paymentMethod: payload.paymentMethod
                });
            })
            .handle(SetDepositMinAmount, (state: DepositReduxState, {payload}: Action<SetDepositMinAmountPayload>) => {
                return Object.assign({}, state, {
                    minAmount: payload.amount,
                });
            })
            .handle(SetPredefinedAmounts, (state: DepositReduxState, {payload}: Action<SetPredefinedAmountsPayload>) => {
                return Object.assign({}, state, {
                    predefinedAmounts: payload.amounts,
                });
            })
            .handle(SetCreditCardProvider, (state: DepositReduxState, {payload}: Action<SetCreditCardProviderPayload>) => {
                this._logger.debug("Set credit card provider: " + payload.creditCardType);
                return Object.assign({}, state, {
                    creditCardType: payload.creditCardType,
                    maxCardLength: payload.maxCardLength || DEF_CREDIT_CARD_LENGTH
                });
            })
            .handle(SetCreditCard, (state: DepositReduxState, {payload}: Action<SetCreditCardPayload>) => {
                return Object.assign({}, state, {
                    creditCard: Utils.merge({}, state.creditCard, payload.creditCard)
                });
            })
            .handle(SetCreditCardValid, (state: DepositReduxState, {payload}: Action<SetCreditCardValidPayload>) => {
                return Object.assign({}, state, {
                    creditCardValid: payload.valid
                });
            })
            .handle(SelectDepositBank, (state: DepositReduxState, {payload}: Action<SelectDepositBankPayload>) => {
                return Object.assign({}, state, {
                    wireTransfer: Utils.merge({}, state.wireTransfer, {
                        selectedBank: payload.bank,
                        selectedBankAccount: payload.bank.account
                    })
                });
            })
            .handle(SelectDepositBankAccount, (state: DepositReduxState, {payload}: Action<SelectDepositBankAccountPayload>) => {
                return Object.assign({}, state, {
                    wireTransfer: Utils.merge({}, state.wireTransfer, {
                        selectedBankAccount: payload.bankAccount
                    })
                });
            })
            .handle(SetWireTransferCurrency, (state: DepositReduxState, {payload}: Action<SetWireTransferCurrencyPayload>) => {
                return Object.assign({}, state, {
                    wireTransferCurrency: payload.wireTransferCurrency
                });
            })
            .handle(SetPaymentMethodAmount, (state: DepositReduxState, {payload}: Action<SetPaymentMethodAmountPayload>) => {
                return Object.assign({}, state, {
                    paymentMethodAmount: Utils.merge({}, state.paymentMethodAmount, payload.paymentMethodAmount)
                });
            });
    }
}
